import Constants from '@/Constants';
import { useProgramClass } from '@/hooks/useProgramClass';
import { LanguageType } from '@/models/LanguageType';
import List from '@/models/List';
import { ProgramClass } from '@/models/ProgramClass';
import { OnChangeProps } from '@/models/typings/ReactDateRange';
import { Workshop, WorkshopTip } from '@/models/Workshop';
import { WorkshopMeeting } from '@/models/WorkshopMeeting';
import DatePicker from '@/pages/Contents/LMS/Classes/DatePicker';
import { addZeroToLeft, currentDate, getHoursList, getNewDate, momentWithTimeZone, resolveLocale } from '@/services/UtilService';
import { ApplicationState } from '@/store';
import { differenceInMinutes, intervalToDuration, isBefore, isSameDay } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

interface Props {
    startDateForm: Date | undefined;
    finishDateForm: Date | undefined;
    onChange: (meeting: WorkshopMeeting[]) => void;
}

type MeetingSelectedTime = {
    startTimeIndex: number;
    endTimeIndex: number;
    startTimeOption: string;
    endTimeOption: string;
    meetingIndex: number;
};

enum DateType {
    START = 'START',
    END = 'END',
}

const WorkShopMeeting: React.FC<Props> = ({ startDateForm, finishDateForm, onChange }: Props) => {
    const { courseId: courseIdString, classId: classIdString } = useParams<{ courseId: string; classId: string }>();

    const courseId = Number(courseIdString);
    const classId = Number(classIdString);

    const workShopInitialState: Workshop = {
        id: undefined,
        programClass: { id: Number(classId) } as ProgramClass,
        course: { id: Number(courseId) } as List,
        tips: [] as WorkshopTip[],
        address: '',
        host: '',
        meetings: [] as WorkshopMeeting[],
    } as Workshop;

    const workshopMeetingInitialState: WorkshopMeeting = ({
        id: undefined,
        startDate: undefined,
        endDate: undefined,
        daysToStart: undefined,
        workshop: workShopInitialState,
    } as unknown) as WorkshopMeeting;

    const strings = useSelector((state: ApplicationState) => state.locale.data);
    const colors: string[] = useSelector((state: ApplicationState) => state.appOrganization.data.colors);
    const appLocale: LanguageType = useSelector((state: ApplicationState) => state.locale.data._LANGUAGE);

    const [currentMeetingIndex, setClickedMeetingIndex] = useState<number>(-1);
    const [currentMeetingDate, setCurrentMeetingDate] = useState<Date | undefined>(undefined);
    const [selectedTimes, setSelectedTimes] = useState<MeetingSelectedTime[]>([]);
    const [meetings, setMeetings] = useState<WorkshopMeeting[]>([workshopMeetingInitialState]);

    const { programClass } = useProgramClass({
        programClassId: classId,
        withEnrollmentsCount: true,
        withWorkshopContent: true,
    });

    const workshopProgramClass: Workshop | undefined = programClass?.workshops && programClass.workshops[0];

    const workshop: Workshop | undefined = !!courseId
        ? programClass?.workshops?.find((workshop: Workshop) => workshop.course.id === courseId)
        : workshopProgramClass;

    const enableInsertButton: boolean =
        meetings.every((meeting) => differenceInMinutes(meeting.endDate, meeting.startDate) > 0) &&
        meetings.some((meeting) => differenceInMinutes(meeting.startDate, currentDate()) > 0);

    const minDate: Date = !startDateForm || (startDateForm && isBefore(startDateForm, currentDate())) ? currentDate() : new Date(startDateForm);

    const maxDate: Date | undefined = finishDateForm ? new Date(finishDateForm) : undefined;

    const isDisabled = (meeting: WorkshopMeeting): boolean =>
        !startDateForm || Boolean(meeting.id && meeting.startDate && isBefore(meeting.startDate, currentDate()));

    const getHours = (date: Date): string[] => {
        const today = currentDate();

        // For Workshops that start at the current day, it just allowed to start 1 Hour after current time
        if (!!date && isSameDay(date, today)) {
            const skipSlices = today.getMinutes().valueOf() / 15;
            return getHoursList(today.getHours().valueOf() + 1, 24, 15, skipSlices);
        }

        return getHoursList();
    };

    const getWorkload = (meeting: WorkshopMeeting): string => {
        let totalDiffBetweenTime: Duration | undefined;

        if (isBefore(meeting.startDate, meeting.endDate)) {
            totalDiffBetweenTime = intervalToDuration({ end: meeting.endDate, start: meeting.startDate });
        }

        let workload: string = '';

        if (totalDiffBetweenTime) {
            const { hours, minutes } = totalDiffBetweenTime;

            if (hours !== 0) {
                workload += `${totalDiffBetweenTime.hours}h`;
            }

            if (minutes !== 0) {
                workload += `${totalDiffBetweenTime.minutes}${hours !== 0 ? '' : 'min'}`;
            }
        }

        return workload;
    };

    const initializeMeetings = (): void => {
        if (workshop && workshop.meetings) {
            setMeetings(workshop.meetings);
        }
    };

    const updateMeetingTime = (date: Date, time: string): Date => {
        const [hour, minute] = time.split(':');
        date?.setHours(Number(hour), Number(minute));
        return date;
    };

    const getSelectedTimeMeeting = (meeting: WorkshopMeeting, type: DateType) => {
        const date: Date | undefined = getNewDate(type === DateType.START ? meeting.startDate : meeting.endDate);

        if (date) {
            const formatedtime: string = `${addZeroToLeft(date.getHours())}:${addZeroToLeft(date.getMinutes())}`;
            const hoursListIndex: number = getHours(date).indexOf(formatedtime);
            const timeIndex: number = meeting.id && hoursListIndex === -1 ? 0 : hoursListIndex;

            if (type === DateType.START) {
                return { startTimeIndex: timeIndex, startTimeOption: formatedtime } as MeetingSelectedTime;
            } else if (type === DateType.END && date.getHours() + date.getMinutes() > 0) {
                return { endTimeIndex: timeIndex, endTimeOption: formatedtime } as MeetingSelectedTime;
            }
        }

        return {} as MeetingSelectedTime;
    };

    const getDateInEnglishFormat = (date: Date) => momentWithTimeZone(date, 'en', false, false, true);

    const handleOnMeetingsChange = (): void => {
        setSelectedTimes(
            meetings.map((meeting: WorkshopMeeting, index: number) => ({
                ...getSelectedTimeMeeting(meeting, DateType.START),
                ...getSelectedTimeMeeting(meeting, DateType.END),
                meetingIndex: index,
            })),
        );

        onChange([...meetings]);
    };

    const handleOnClickMeetingDate = (meetingIndex: number, date: Date) => {
        setClickedMeetingIndex(meetingIndex);
        setCurrentMeetingDate(new Date(date));
    };

    const handleOnChangeMeetingDate = (value: OnChangeProps) => {
        const meeting: WorkshopMeeting | undefined = meetings[currentMeetingIndex];

        setCurrentMeetingDate(undefined);

        if (meeting) {
            const date: Date = value as Date;
            meeting.id = (undefined as unknown) as number;
            meeting.startDate = new Date(date);
            meeting.startDateAsString = getDateInEnglishFormat(date);
            meeting.endDate = new Date(date);
            meeting.endDateAsString = getDateInEnglishFormat(date);
            setMeetings([...meetings]);
        }
    };

    const handleOnClearMeetingDate = (meetingIndex: number) => {
        if (meetings[meetingIndex]) {
            meetings[meetingIndex] = ({ ...meetings[meetingIndex], startDate: undefined, endDate: undefined } as unknown) as WorkshopMeeting;
            selectedTimes[meetingIndex] = { meetingIndex } as MeetingSelectedTime;
            setMeetings([...meetings]);
        }
    };

    const handleOnInsertNewMeeting = () => {
        const newMeeting = ({ startDate: undefined, endDate: undefined, workshop: { id: workshop?.id } } as unknown) as WorkshopMeeting;
        setMeetings([...meetings, newMeeting]);
    };

    const handleOnRemoveMeeting = (meetingDayIndex: number) => {
        const meetingsAux: WorkshopMeeting[] = [...meetings];
        meetingsAux.splice(meetingDayIndex, 1);
        setMeetings(meetingsAux);
    };

    const handleOnSelectMeetingTime = (time: string, meetingIndex: number, isDisabled: boolean, dateType: DateType) => {
        if (!isDisabled) {
            if (dateType === DateType.START) {
                const startDate: Date = new Date(meetings[meetingIndex].startDate);
                meetings[meetingIndex].startDate = updateMeetingTime(startDate, time);
                meetings[meetingIndex].startDateAsString = getDateInEnglishFormat(startDate);
            } else {
                const endDate: Date = new Date(meetings[meetingIndex].endDate);
                meetings[meetingIndex].endDate = updateMeetingTime(endDate, time);
                meetings[meetingIndex].endDateAsString = getDateInEnglishFormat(endDate);
            }
            setMeetings([...meetings]);
        }
    };

    const handleOnStartDateFormChange = () => {
        if (!startDateForm && selectedTimes.length > 0) {
            setMeetings([workshopMeetingInitialState]);
        }
    };

    useEffect(initializeMeetings, [workshop]);

    useEffect(handleOnMeetingsChange, [meetings]);

    useEffect(handleOnStartDateFormChange, [startDateForm]);

    return (
        <div className="meeting-container">
            <p>{strings.WORKSHOP_CLASS_PERIOD}</p>

            {meetings.map((meeting: WorkshopMeeting, meetingIndex: number) => {
                const selectedTime = selectedTimes[meetingIndex];
                const startDateHours: string[] = getHours(meeting.startDate);
                const endDateHours: string[] = getHours(meeting.endDate);

                return (
                    <div className="container-buttons workshop" key={`meeting_${meetingIndex}`}>
                        <div className="workshop-meeting-info">
                            <span>{strings.WORKSHOP_MEETING_DAY_NUMBER.replace('{0}', String(meetingIndex + 1))}</span>
                        </div>

                        <button
                            className={`btn btn-primary datetimepicker date ${meeting.startDate ? 'selected' : ''}`}
                            disabled={isDisabled(meeting)}
                            onClick={() => handleOnClickMeetingDate(meetingIndex, meeting.startDate || startDateForm)}
                        >
                            <i className="material-icons-outlined align-middle">{Constants.icons.CALENDAR_TODAY}</i>

                            {meeting.startDate ? (
                                <>
                                    <span className="m-left--xs m-right--xs">
                                        {meeting.startDate && momentWithTimeZone(meeting.startDate, strings._LANGUAGE)}
                                    </span>
                                    <i
                                        className="material-icons-outlined btn-calendar-close clear-date"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleOnClearMeetingDate(meetingIndex);
                                        }}
                                    >
                                        {Constants.icons.CLOSE}
                                    </i>
                                </>
                            ) : (
                                <span className="m-left--xs">{strings.WORKSHOP_CHOOSE_START_DATE}</span>
                            )}
                        </button>

                        <div className="container-buttons__date-time">
                            <div className="dropdown-option-container">
                                <div className="dropdown bordered">
                                    <button
                                        aria-expanded="false"
                                        aria-haspopup="true"
                                        className={`btn btn-primary btn-sm font-weight-bold ${
                                            selectedTime?.startTimeIndex === undefined || selectedTime?.startTimeIndex === -1 ? '' : 'selected'
                                        }`}
                                        data-toggle="dropdown"
                                        disabled={!meeting.startDate || isDisabled(meeting)}
                                        id="dropdownMenuButton"
                                        type="button"
                                    >
                                        <i className="material-icons-outlined align-middle">{Constants.icons.ACCESS_TIME}</i>
                                        <>
                                            <span className="selected-start-time">
                                                {strings.WORKSHOP_SELECTED_START_TIME}
                                                <i>|</i>
                                                {selectedTime?.startTimeOption ? selectedTime?.startTimeOption : '00:00'}
                                            </span>
                                            <i className="material-icons-outlined align-middle arrow-icon">{Constants.icons.KEYBOARD_ARROW_DOWN}</i>
                                        </>
                                    </button>

                                    <div aria-labelledby="dropdownMenuButton" className="dropdown-menu">
                                        <ul>
                                            {startDateHours.map((time: string, index: number) => {
                                                const isDisabled: boolean = selectedTime?.endTimeIndex > -1 && selectedTime?.endTimeIndex <= index;
                                                const isSelected: boolean =
                                                    selectedTime?.startTimeIndex !== -1 && selectedTime?.startTimeOption === time;

                                                return (
                                                    <li key={`startHour_${index}`}>
                                                        <input
                                                            className="radio"
                                                            disabled={isDisabled}
                                                            id={`end-time-${index}`}
                                                            name="time"
                                                            required
                                                            type="radio"
                                                        />

                                                        <label
                                                            className={isSelected ? 'selected' : ''}
                                                            htmlFor={`start-time-${index}`}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                e.preventDefault();
                                                                handleOnSelectMeetingTime(time, meetingIndex, isDisabled, DateType.START);
                                                            }}
                                                        >
                                                            {time}
                                                        </label>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            <div className="dropdown-option-container">
                                <div className="dropdown bordered">
                                    <button
                                        aria-expanded="false"
                                        aria-haspopup="true"
                                        className={`btn btn-primary btn-sm font-weight-bold ${
                                            selectedTime?.endTimeIndex > -1 && selectedTime?.endTimeOption !== '00:00' ? 'selected' : ''
                                        }`}
                                        data-toggle="dropdown"
                                        disabled={selectedTime?.startTimeIndex === -1 || !meeting.startDate || isDisabled(meeting)}
                                        id="dropdownMenuButton"
                                        type="button"
                                    >
                                        <i className="material-icons-outlined align-middle">{Constants.icons.ACCESS_TIME}</i>

                                        <>
                                            <span className="selected-end-time">
                                                {strings.WORKSHOP_SELECTED_END_TIME}
                                                <i>|</i>
                                                {selectedTime?.endTimeOption ? selectedTime?.endTimeOption : '00:00'}
                                            </span>
                                            <i className="material-icons-outlined align-middle arrow-icon">{Constants.icons.KEYBOARD_ARROW_DOWN}</i>
                                        </>
                                    </button>

                                    <div aria-labelledby="dropdownMenuButton" className="dropdown-menu">
                                        <ul>
                                            {endDateHours.map((time: string, index: number) => {
                                                const isDisabled: boolean = selectedTime?.startTimeIndex >= index;
                                                const isSelected: boolean = selectedTime?.endTimeIndex !== -1 && selectedTime?.endTimeOption === time;

                                                return (
                                                    <li key={`endHour_${index}`}>
                                                        <input
                                                            className="radio"
                                                            disabled={isDisabled}
                                                            id={`end-time-${index}`}
                                                            name="time"
                                                            required
                                                            type="radio"
                                                        />

                                                        <label
                                                            className={isSelected ? 'selected' : undefined}
                                                            htmlFor={`end-time-${index}`}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                e.preventDefault();
                                                                handleOnSelectMeetingTime(time, meetingIndex, isDisabled, DateType.END);
                                                            }}
                                                        >
                                                            {time}
                                                        </label>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                </div>

                                <div className="workshop-workload">
                                    <span className="workshop-period-section-info">
                                        {isBefore(meeting.startDate, meeting.endDate) ? strings.WORKSHOP_TIME : ' '}
                                        <strong>{isBefore(meeting.startDate, meeting.endDate) && getWorkload(meeting)}</strong>
                                    </span>
                                </div>
                            </div>
                        </div>

                        {meetingIndex > 0 && !isDisabled(meeting) && (
                            <div className="form-group inner-addon container-workshop-tips__tips-box">
                                <i
                                    className="material-icons-outlined align-middle input-icon trash-icon"
                                    onClick={() => handleOnRemoveMeeting(meetingIndex)}
                                >
                                    {Constants.icons.TRASH_CAN}
                                </i>
                            </div>
                        )}
                    </div>
                );
            })}

            <div className="workshop-add-meeting">
                <button
                    className={`btn container-workshop-tips__add-tips ${enableInsertButton ? '' : 'disabled'}`}
                    disabled={!enableInsertButton}
                    onClick={handleOnInsertNewMeeting}
                    type="button"
                >
                    <i className="material-icons-outlined align-middle">{Constants.icons.CIRCLE_PLUS}</i>
                    {strings.WORKSHOP_ADD_NEW_MEETING_DAY}
                </button>
            </div>

            <DatePicker
                color={colors[0]}
                initialDate={currentMeetingDate}
                locale={resolveLocale(appLocale)}
                maxDate={maxDate}
                minDate={minDate}
                onChange={handleOnChangeMeetingDate}
                onHide={() => setCurrentMeetingDate(undefined)}
                show={!!currentMeetingDate}
                title={strings.START_DATE}
                weekdayDisplayFormat={'EEEEEE'}
            />
        </div>
    );
};

export default WorkShopMeeting;
